import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

export default function EmailCapture({
  variant = 'inline',
  source = 'general',
  title,
  subtitle,
}) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const defaultTitle = variant === 'card'
    ? 'Stay ahead of the market — free'
    : 'Get the KnowFi Brief in your inbox — free';

  const defaultSubtitle = variant === 'card'
    ? 'Plain English market updates every morning.'
    : 'Plain English market updates every morning.';

  const displayTitle = title || defaultTitle;
  const displaySubtitle = subtitle || defaultSubtitle;

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch(`${API}/api/email-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Something went wrong. Try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div style={variant === 'card' ? styles.cardWrap : styles.inlineWrap}>
        <div style={styles.successBox}>
          <span style={styles.successText}>✅ You're in! Check your inbox soon.</span>
        </div>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div style={styles.cardWrap}>
        <div style={styles.cardInner}>
          <div style={styles.cardIcon}>📬</div>
          <h3 style={styles.cardTitle}>{displayTitle}</h3>
          <p style={styles.cardSubtitle}>{displaySubtitle}</p>
          <form onSubmit={handleSubmit} style={styles.cardForm}>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
              placeholder="your@email.com"
              style={styles.input}
              disabled={status === 'loading'}
            />
            <button
              type="submit"
              style={{ ...styles.btn, ...(status === 'loading' ? styles.btnDisabled : {}) }}
              disabled={status === 'loading'}
            >
              {status === 'loading' ? 'Sending…' : 'Get The Brief Free →'}
            </button>
          </form>
          {status === 'error' && <div style={styles.errorMsg}>{errorMsg}</div>}
          <p style={styles.noSpam}>No spam. Unsubscribe anytime.</p>
        </div>
      </div>
    );
  }

  // inline variant
  return (
    <div style={styles.inlineWrap}>
      <div style={styles.inlineInner}>
        <div style={styles.inlineLeft}>
          <div style={styles.inlineTitle}>{displayTitle}</div>
          <div style={styles.inlineSubtitle}>{displaySubtitle}</div>
        </div>
        <form onSubmit={handleSubmit} style={styles.inlineForm}>
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
            placeholder="your@email.com"
            style={styles.input}
            disabled={status === 'loading'}
          />
          <button
            type="submit"
            style={{ ...styles.btn, ...(status === 'loading' ? styles.btnDisabled : {}) }}
            disabled={status === 'loading'}
          >
            {status === 'loading' ? 'Sending…' : 'Get The Brief Free →'}
          </button>
        </form>
        {status === 'error' && <div style={{ ...styles.errorMsg, width: '100%' }}>{errorMsg}</div>}
      </div>
    </div>
  );
}

const styles = {
  cardWrap: {
    padding: '48px 20px',
    display: 'flex',
    justifyContent: 'center',
  },
  cardInner: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '20px',
    padding: '40px 36px',
    maxWidth: '480px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  cardIcon: {
    fontSize: '40px',
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#f1f5f9',
    marginBottom: '10px',
    lineHeight: 1.3,
  },
  cardSubtitle: {
    fontSize: '15px',
    color: '#94a3b8',
    marginBottom: '24px',
    lineHeight: 1.6,
  },
  cardForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '8px',
  },
  inlineWrap: {
    background: 'linear-gradient(135deg, rgba(56,189,248,0.06) 0%, rgba(99,102,241,0.04) 100%)',
    borderTop: '1px solid #1e293b',
    borderBottom: '1px solid #1e293b',
    padding: '40px 20px',
  },
  inlineInner: {
    maxWidth: '900px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '32px',
    flexWrap: 'wrap',
  },
  inlineLeft: {
    flex: 1,
    minWidth: '200px',
  },
  inlineTitle: {
    fontSize: '20px',
    fontWeight: '800',
    color: '#f1f5f9',
    marginBottom: '6px',
    lineHeight: 1.3,
  },
  inlineSubtitle: {
    fontSize: '14px',
    color: '#64748b',
    lineHeight: 1.5,
  },
  inlineForm: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    flexShrink: 0,
    width: '100%',
  },
  input: {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '10px',
    color: '#f1f5f9',
    fontSize: '14px',
    padding: '12px 16px',
    outline: 'none',
    flex: '1 1 180px',
    minWidth: '0',
    fontFamily: 'inherit',
  },
  btn: {
    background: '#f59e0b',
    color: '#0f172a',
    border: 'none',
    borderRadius: '10px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    transition: 'opacity 0.15s',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  errorMsg: {
    color: '#f87171',
    fontSize: '13px',
    marginTop: '8px',
  },
  noSpam: {
    color: '#475569',
    fontSize: '11px',
    marginTop: '12px',
  },
  successBox: {
    background: 'rgba(34,197,94,0.1)',
    border: '1px solid rgba(34,197,94,0.25)',
    borderRadius: '12px',
    padding: '20px 28px',
    textAlign: 'center',
    maxWidth: '480px',
    margin: '0 auto',
  },
  successText: {
    color: '#4ade80',
    fontSize: '16px',
    fontWeight: '600',
  },
};
